import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSnackbar } from '../contexts/SnackbarProvider';
import { SnackbarType } from '../components/Snackbar/types';
import { VideoImage, TempVideoImage, ImageOrderItem } from '../services/videoImageApi/videoImageApi.types';
import { batchDeleteVideoImages, updateVideoImages, createVideoImage } from '../services/videoImageApi/videoImageApi';

/**
 * 動画編集画面の画像管理用カスタムフック
 * 削除予定→保存時一括削除のフローを実現
 */
export const useVideoEditImages = (videoId: string, initialImages: VideoImage[] = []) => {
  const showSnackbar = useSnackbar();
  
  // 表示用の全画像（削除予定も含む）
  const [images, setImages] = useState<VideoImage[]>(initialImages);
  
  // 削除予定の画像IDセット
  const [pendingDeletions, setPendingDeletions] = useState<Set<string>>(new Set());
  
  // 新規追加予定の画像リスト
  const [pendingAdditions, setPendingAdditions] = useState<TempVideoImage[]>([]);
  
  // 初期画像が変更された場合に状態を更新（初回のみ）
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastVideoId, setLastVideoId] = useState(videoId);
  
  useEffect(() => {
    // videoIdが変更された場合は初期化をリセット
    if (videoId !== lastVideoId) {
      setIsInitialized(false);
      setLastVideoId(videoId);
      setImages([]);
      setPendingDeletions(new Set());
      setPendingAdditions([]);
    }
  }, [videoId, lastVideoId]);
  
  useEffect(() => {    
    // 初回初期化
    if (!isInitialized && initialImages.length >= 0) {
      setImages(initialImages);
      setIsInitialized(true);
      return;
    }
    
    // 初期化済みで、変更がない場合のみ、initialImagesが変更されたら更新
    if (isInitialized && pendingDeletions.size === 0 && pendingAdditions.length === 0) {
      if (initialImages.length > 0 && images.length === 0) {
        // imagesが空でinitialImagesにデータがある場合（画像取得後）
        setImages(initialImages);
      } else if (initialImages.length > 0 && images.length !== initialImages.length) {
        // 画像数が異なる場合（保存後など）
        setImages(initialImages);
      }
    }
  }, [initialImages, isInitialized, images.length, pendingDeletions.size, pendingAdditions.length]);
  
  // 保存処理中フラグ
  const [isSaving, setIsSaving] = useState(false);
  

  // 削除予定を除いた実際の画像リスト
  const activeImages = useMemo(() => {
    return images.filter(img => !pendingDeletions.has(img.id));
  }, [images, pendingDeletions]);


  // 削除予定の切り替え
  const toggleDeletionStatus = useCallback((imageId: string) => {
    setPendingDeletions(prev => {
      const newSet = new Set(prev);
      const wasPending = newSet.has(imageId);
      if (wasPending) {
        newSet.delete(imageId);
        // showSnackbarをuseEffectで呼び出すように変更
        setTimeout(() => {
          showSnackbar('削除予定をキャンセルしました', SnackbarType.INFO);
        }, 0);
      } else {
        newSet.add(imageId);
        // showSnackbarをuseEffectで呼び出すように変更
        setTimeout(() => {
          showSnackbar('画像を削除予定に追加しました（保存時に確定されます）', SnackbarType.INFO);
        }, 0);
      }
      return newSet;
    });
  }, [showSnackbar]);

  // 画像が削除予定かどうか
  const isPendingDeletion = useCallback((imageId: string) => {
    return pendingDeletions.has(imageId);
  }, [pendingDeletions]);

  // 新規画像を追加
  const addImage = useCallback((newImage: TempVideoImage) => {
    setImages(prev => {
      const newImages = [...prev, newImage];
      return newImages;
    });
    setPendingAdditions(prev => {
      const newPendingAdditions = [...prev, newImage];
      return newPendingAdditions;
    });
  }, []);

  // 画像を更新
  const updateImage = useCallback((imageId: string, updates: Partial<VideoImage>) => {
    setImages(prev => prev.map(img => 
      img.id === imageId ? { ...img, ...updates } : img
    ));
  }, []);

  // 一時保存画像を削除（即座にリストから除去）
  const removeImage = useCallback((imageId: string) => {
    // imagesから削除
    setImages(prev => {
      const newImages = prev.filter(img => img.id !== imageId);
      return newImages;
    });
    
    // pendingAdditionsからも削除（一時保存画像の場合）
    setPendingAdditions(prev => {
      const newPendingAdditions = prev.filter(img => img.id !== imageId);
      return newPendingAdditions;
    });
    
    // pendingDeletionsからも削除（削除予定に含まれている場合）
    setPendingDeletions(prev => {
      const newSet = new Set(prev);
      newSet.delete(imageId);
      return newSet;
    });
    
    showSnackbar('画像を削除しました', SnackbarType.INFO);
  }, [showSnackbar]);

  // 画像の順序を更新
  const reorderImages = useCallback((newOrder: VideoImage[]) => {
    setImages(newOrder);
  }, []);

  // 保存時の一括処理
  const saveChanges = useCallback(async () => {
    if (pendingDeletions.size === 0 && pendingAdditions.length === 0) {
      return { success: true, message: '変更はありません' };
    }

    // 保存後の画像数をチェック
    // activeImagesには既に一時画像が含まれているため、pendingAdditionsを追加しない
    const finalImageCount = activeImages.length;
    
    if (finalImageCount > 5) {
      showSnackbar(
        `画像は最大5枚までです。現在の画像数: ${images.length}枚（削除予定: ${pendingDeletions.size}枚を除くと${finalImageCount}枚になります）`,
        SnackbarType.WARNING
      );
      return { 
        success: false, 
        message: '画像数が最大数を超えています'
      };
    }

    setIsSaving(true);
    
    try {
      // 削除予定のIDセットを作成（後で除外するため）
      const deletionSet = new Set(pendingDeletions);
      
      // images配列から順序情報を生成
      const imageOrder: ImageOrderItem[] = images
        .filter(img => !pendingDeletions.has(img.id))
        .map(img => {
          if (img.id.startsWith('temp_')) {
            return { type: 'new' as const, temp_id: img.id };
          } else {
            return { type: 'existing' as const, id: img.id };
          }
        });
      
      // 削除する画像ID（temp_で始まらないもののみ）
      const deleteImageIds = Array.from(pendingDeletions).filter(id => !id.startsWith('temp_'));
      
      // 新規追加予定の画像（削除予定でないもの）
      const validPendingAdditions = pendingAdditions.filter(img => !deletionSet.has(img.id));
      
      // 新規画像ファイル、一時ID、説明文を準備
      const newImages: File[] = [];
      const newImagesTempIds: string[] = [];
      const newImagesDescriptions: string[] = [];
      
      validPendingAdditions.forEach(tempImage => {
        if (tempImage.file) {
          newImages.push(tempImage.file);
          newImagesTempIds.push(tempImage.id);
          newImagesDescriptions.push(tempImage.description || '');
        }
      });
      
      // バックエンドに一括更新リクエスト
      const result = await updateVideoImages(videoId, {
        image_order: imageOrder,
        delete_image_ids: deleteImageIds,
        new_images: newImages,
        new_images_temp_ids: newImagesTempIds,
        new_images_descriptions: newImagesDescriptions
      });
      
      // APIレスポンスから返却された画像情報を使って状態を更新
      setImages(result.updated_images.map(img => ({
        id: String(img.id),
        video_id: String(img.video_id),
        image_path: img.image_path,
        description: img.description || '',
        display_order: img.display_order,
        created_at: img.created_at,
        updated_at: img.updated_at
      })));
      setPendingDeletions(new Set());
      setPendingAdditions([]);
      
      const actualDeletedCount = result.deleted_count;
      const actualAddedCount = result.added_count;
      
      showSnackbar(
        `画像の更新が完了しました（削除: ${actualDeletedCount}枚、追加: ${actualAddedCount}枚）`, 
        SnackbarType.SUCCESS
      );
      
      return { 
        success: true, 
        message: `画像の更新が完了しました（削除: ${actualDeletedCount}枚、追加: ${actualAddedCount}枚）`,
        deletedCount: actualDeletedCount,
        addedCount: actualAddedCount
      };
      
    } catch (error) {
      console.error('画像保存エラー:', error);
      
      // エラーメッセージを抽出
      let errorMessage = '画像の保存に失敗しました';
      
      if (error && typeof error === 'object') {
        if ('response' in error && error.response) {
          const response = error.response as any;
          if (response.data?.error) {
            errorMessage = response.data.error;
          }
        } else if ('message' in error) {
          errorMessage = (error as Error).message;
        }
      }
      
      showSnackbar(errorMessage, SnackbarType.ERROR);
      return { success: false, message: errorMessage };
    } finally {
      setIsSaving(false);
    }
  }, [videoId, images, pendingDeletions, pendingAdditions, showSnackbar]);

  // 変更をリセット
  const resetChanges = useCallback(() => {
    setPendingDeletions(new Set());
    setPendingAdditions([]);
    showSnackbar('変更をリセットしました', SnackbarType.INFO);
  }, [showSnackbar]);

  // 変更があるかどうか
  const hasChanges = useMemo(() => {
    const hasDeletions = pendingDeletions.size > 0;
    const hasAdditions = pendingAdditions.length > 0;
    return hasDeletions || hasAdditions;
  }, [pendingDeletions.size, pendingAdditions.length]);

  return {
    // 状態
    images,
    activeImages,
    pendingDeletions: Array.from(pendingDeletions),
    pendingAdditions,
    isSaving,
    hasChanges,
    
    // アクション
    toggleDeletionStatus,
    isPendingDeletion,
    addImage,
    updateImage,
    removeImage,
    reorderImages,
    saveChanges,
    resetChanges,
  };
};
